/**
 * Inline-image extraction for outgoing mail.
 *
 * The compose editors embed pasted/inlined images as `data:` URIs — that's
 * what renders inside a WebView editor with no server round-trip. But a
 * `data:` URI inside the text/html MIME part is NOT interoperable mail:
 * Outlook desktop (Word renderer) doesn't render data: images at all, and
 * Gmail's web sanitizer strips them, so recipients on the two biggest
 * platforms see broken/missing images while Thunderbird/Apple Mail users see
 * the message fine. The interoperable form is RFC 2392 `cid:` references to
 * base64 image parts inside a multipart/related wrapper — what every mail
 * client has emitted since the 90s.
 *
 * This helper rewrites `<img src="data:...;base64,...">` to `cid:` refs and
 * hands back the extracted parts; the platform send() paths (desktop
 * mailx-service and Android/web web-service) wrap them into
 * multipart/related. Shared here (mailx-types, zero deps) so both MIME
 * assemblers can't drift apart.
 *
 * Non-base64 data: URIs (utf8 SVG etc.) are left in place — rare, small,
 * and rewriting them would mean re-encoding; the size/interop problem this
 * solves comes from pasted photos, which are always base64.
 */

export interface InlineImagePart {
    /** Content-ID value WITHOUT angle brackets (use `<${contentId}>` in the header). */
    contentId: string;
    mime: string;
    base64: string;
}

/** Pull base64 `data:` images out of the HTML, replacing each src with a
 *  `cid:` reference. Identical data: URIs (the same image pasted twice)
 *  collapse to one part. `idDomain` scopes the Content-IDs (RFC 2392 wants
 *  msg-id-like uniqueness). */
/** Base64 of a UTF-8 string, in both runtimes this package targets: the
 *  daemon (Node, no `btoa` on older majors) and the browser/WebView (no
 *  `Buffer`). SVG markup is text, so it must go through UTF-8 bytes rather
 *  than `btoa` directly, which throws on any character above U+00FF. */
function utf8ToBase64(text: string): string {
    const bytes = new TextEncoder().encode(text);
    // Reached through globalThis, not by name: this module is type-checked
    // against the BROWSER lib for the client build, where `Buffer` has no
    // declaration at all.
    const B = (globalThis as any).Buffer;
    if (B) return B.from(bytes).toString("base64");
    let bin = "";
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin);
}

export function extractInlineImages(html: string, idDomain: string): { html: string; images: InlineImagePart[] } {
    const images: InlineImagePart[] = [];
    const byDataUri = new Map<string, string>();   // data-uri → contentId
    const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    // Two encodings, one job. `;base64,` is what an editor produces when it
    // ingests a pasted bitmap — but SVG normally arrives URL-ENCODED
    // (`data:image/svg+xml,%3Csvg…`), which is how most tools export it and
    // what you get pasting markup. Matching only the base64 form left those
    // as raw `data:` URIs on the wire: invisible in Outlook, stripped by
    // Gmail — exactly the breakage v1.2.179 fixed for bitmaps, still live for
    // SVG until now (Bob 2026-08-09: "can I put an SVG image into a
    // message?"). Percent-encoded payloads are decoded and re-encoded to
    // base64 so every inline image leaves as a normal MIME part.
    const out = html.replace(
        /(<img\b[^>]*?\ssrc=)(["'])(data:(image\/[\w.+-]+)(;base64)?,([^"']+))\2/gi,
        (_m, prefix: string, quote: string, dataUri: string, mime: string, isB64: string | undefined, payload: string) => {
            let cid = byDataUri.get(dataUri);
            if (!cid) {
                let base64: string;
                if (isB64) {
                    // Strip whitespace the editor may have wrapped into the URI;
                    // the send path re-wraps at 76 cols per RFC 2045.
                    base64 = payload.replace(/\s+/g, "");
                } else {
                    try {
                        base64 = utf8ToBase64(decodeURIComponent(payload));
                    } catch {
                        // Malformed percent-encoding — leave the src untouched
                        // rather than emit a corrupt part. Better a broken
                        // image the sender can see in their own Sent copy than
                        // a silently mangled attachment.
                        return _m;
                    }
                }
                if (!base64) return _m;
                cid = `img${images.length + 1}.${stamp}@${idDomain}`;
                byDataUri.set(dataUri, cid);
                images.push({ contentId: cid, mime, base64 });
            }
            return `${prefix}${quote}cid:${cid}${quote}`;
        },
    );
    return { html: out, images };
}
